import { h, Component, Prop, Element, State } from '@stencil/core';

@Component({
  shadow: true,
  styleUrls: {
    default: 'igws-cta.less',
    rtl: 'igws-cta.rtl.less',
    rebrand: './rebrand/igws-cta.less',
    rtl_rebrand: './rebrand/igws-cta.rtl.less',
    prime: './prime/igws-cta.less',
    rtl_prime: './prime/igws-cta.rtl.less',
    investments: 'igws-cta.less',
    rtl_investments: 'igws-cta.rtl.less',
    helpandsupport: 'igws-cta.less',
    rtl_helpandsupport: 'igws-cta.rtl.less',
    academy: 'igws-cta.less',
    rtl_academy: 'igws-cta.rtl.less',
    landingpages: 'igws-cta.less',
    rtl_landingpages: 'igws-cta.rtl.less',
    generic: 'igws-cta.less',
    rtl_generic: 'igws-cta.rtl.less',
  },
  tag: 'igws-cta',
})
export class CallToActionComponent {
  @Element() el!: HTMLIgwsCtaElement;

  /**
   * EU rebrand theme detection - replaces :host-context() for cross-browser compatibility
   */
  @State() hasEuTheme: boolean = false;
  /**
   * EU dark mode theme detection
   */
  @State() hasEuDarkTheme: boolean = false;

  /**
   * MutationObserver to watch for theme changes on body element
   */
  private themeObserver?: MutationObserver;

  /**
   * Cta alignment
   */
  @Prop() align: string = '';

  /**
   * Cta type
   */
  @Prop() type: string = '';

  /**
   * Cta extra classes
   */
  @Prop() classnames: string = '';

  /**
   * Cta href
   */
  @Prop() href: string = '';

  /**
   * Cta target
   */
  @Prop() target: string = '';

  /**
   * Cta text
   */
  @Prop() text: string = '';

  /**
   * Cta iconPrefix
   */
  @Prop() iconPrefix: string = '';

  /**
   * Cta iconSuffix
   */
  @Prop() iconSuffix: string = '';

  /**
   * Cta no follow
   */
  @Prop() nofollow: boolean = false;

  /**
   * Cta ariaLabel
   */
  @Prop() labelAria: string = '';

  componentWillLoad() {
    const crawlLink = this.el && this.el.querySelector('.crawl-link');
    if (crawlLink) {
      this.el.removeChild(crawlLink);
    }

    // Detect EU rebrand theme on body element
    // This replaces :host-context() for cross-browser compatibility (Firefox, Safari)
    this.hasEuTheme = document.body.classList.contains('ig-eu-rebrand-theme');
    this.hasEuDarkTheme = document.body.classList.contains('ig-eu-theme-dark');
  }

  componentDidLoad() {
    // Set up MutationObserver to watch for theme changes on body element
    // This allows the component to react to theme switches without page refresh
    this.themeObserver = new MutationObserver(() => {
      const newDarkThemeState = document.body.classList.contains('ig-eu-theme-dark');
      if (this.hasEuDarkTheme !== newDarkThemeState) {
        this.hasEuDarkTheme = newDarkThemeState;
      }
    });

    // Observe changes to body class attribute
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  disconnectedCallback() {
    // Clean up the observer when component is removed
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  private handleNavigation = (e: MouseEvent) => {
    e.preventDefault();
    if (this.href !== '') {
      window.open(this.href, this.target || '_self');
    }
  };

  render() {
    const rel: string = this.nofollow ? 'nofollow' : '';
    // Add internal EU theme classes based on body class detection
    const euThemeActive: string = this.hasEuTheme ? 'eu-theme-active' : '';
    const euDarkActive: string = this.hasEuDarkTheme ? 'eu-theme-dark-active' : '';

    return (
      <span class={`cta-wrapper ${this.align} ${this.type} ${this.classnames} ${euThemeActive} ${euDarkActive}`} onClick={this.handleNavigation}>
        {this.iconPrefix !== '' ? (
          <div class="prefix-wrapper">
            <img class="prefix" src={this.iconPrefix} alt="icon prefix" />
          </div>
        ) : (
          ''
        )}

        {this.text !== '' ? (
          <a
            class={`cta-anchor ${this.type}`}
            href={this.href}
            target={this.target}
            rel={rel}
            {...(this.labelAria !== '' ? { 'aria-label': this.labelAria } : {})}
          >
            <span class="cta__text" innerHTML={this.text}></span>
          </a>
        ) : (
          ''
        )}

        {this.iconSuffix !== '' ? (
          <div class="suffix-wrapper">
            <img class="suffix" src={this.iconSuffix} alt="icon suffix" />
          </div>
        ) : (
          ''
        )}
      </span>
    );
  }
}
